import type { ProductFormData } from './product.schema';

/**
 * Canonical Product Clone Transformer (Deep Product Clone)
 *
 * Chức năng:
 * 1. Bê nguyên vẹn 100% Loại sản phẩm, Kích thước, Thông tin bài bình (khổ in, khổ xả, số bát),
 *    BOM vật tư (kèm định mức standardQuantity), Quy trình sản xuất (kèm 8 thông số máy setup/tốc độ/bù hao/chi phí).
 * 2. Kế thừa sâu các thành phần con của sản phẩm combo (ProductComponent).
 * 3. TỰ ĐỘNG RESET:
 *    - productName = "" (chuỗi rỗng, buộc người dùng nhập tên mới rõ ràng).
 *    - productCode = "" (để trống để hệ thống tự động sinh theo khách hàng).
 */
export function buildClonedProductFormData(source: any): Partial<ProductFormData> & {
  processIds?: string[];
  productType?: any;
  customer?: any;
} {
  if (!source) return {};

  // 1. Reset các trường nhận diện duy nhất
  const productCode = ''; // Để trống cho hệ thống tự sinh mã theo mã khách hàng
  const productName = ''; // RESET VỀ RỖNG THEO YÊU CẦU NGHIỆP VỤ

  // 2. Kế thừa Loại sản phẩm & Khách hàng
  const customerId = source.customerId || '';
  const customerName =
    source.customer?.shortName ||
    source.customer?.name ||
    source.customerName ||
    '';
  const typeId = source.typeId || '';
  const productType = source.productType || null;
  const customer = source.customer || null;

  // 3. Kế thừa Kích thước phủ bì & Khổ trải (mm)
  const length = source.length != null ? Number(source.length) : undefined;
  const width = source.width != null ? Number(source.width) : undefined;
  const height = source.height != null ? Number(source.height) : undefined;
  const flatLength = source.flatLength != null ? Number(source.flatLength) : undefined;
  const flatWidth = source.flatWidth != null ? Number(source.flatWidth) : undefined;

  // 4. Kế thừa Thông số bài bình (Imposition / Layout)
  const printFormat = source.printFormat || '';
  const cutFormat = source.cutFormat || '';
  const outs = source.outs != null ? Number(source.outs) : undefined;

  // 5. Kế thừa Quy trình sản xuất & Cấu hình máy (100% Process Configs)
  const rawProcesses = source.productProcesses || [];
  const sortedProcesses = Array.isArray(rawProcesses)
    ? [...rawProcesses].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    : [];

  const processIds: string[] = sortedProcesses.length > 0
    ? sortedProcesses.map((pp: any) => pp.processId || pp.process?.id).filter(Boolean)
    : Array.isArray(source.processIds)
    ? source.processIds
    : [];

  const processConfigs = sortedProcesses.length > 0
    ? sortedProcesses
        .map((pp: any) => ({
          processId: pp.processId || pp.process?.id,
          setupTimeMinutes: pp.setupTimeMinutes ?? null,
          speedPerHour: pp.speedPerHour ?? null,
          runWastePercent: pp.runWastePercent != null ? Number(pp.runWastePercent) : null,
          setupWasteQty: pp.setupWasteQty != null ? Number(pp.setupWasteQty) : null,
          isOutsourced: Boolean(pp.isOutsourced),
          unitCost: pp.unitCost != null ? Number(pp.unitCost) : null,
          batchCost: pp.batchCost != null ? Number(pp.batchCost) : null,
          moq: pp.moq != null ? Number(pp.moq) : null,
        }))
        .filter((c: any) => Boolean(c.processId))
    : Array.isArray(source.processConfigs)
    ? source.processConfigs
    : [];

  // 6. Kế thừa Chất liệu & BOM (100% Material Configs & Standard Quantity)
  const rawMaterials = source.productMaterials || [];
  const sortedMaterials = Array.isArray(rawMaterials)
    ? [...rawMaterials].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    : [];

  const materialIds: string[] = sortedMaterials.length > 0
    ? sortedMaterials.map((pm: any) => pm.materialId || pm.material?.id).filter(Boolean)
    : source.materialId
    ? [source.materialId]
    : Array.isArray(source.materialIds)
    ? source.materialIds
    : [];

  const productMaterials = sortedMaterials.length > 0
    ? sortedMaterials.map((pm: any) => ({
        materialId: pm.materialId || pm.material?.id,
        processId: pm.process?.processId || pm.productProcess?.processId || pm.processId || '',
        standardQuantity: pm.standardQuantity != null ? Number(pm.standardQuantity) : 0,
        note: pm.note || '',
      }))
    : source.materialId
    ? [{ materialId: source.materialId, processId: '', standardQuantity: 1, note: '' }]
    : Array.isArray(source.productMaterials)
    ? source.productMaterials
    : [];

  // 7. Kế thừa Thành phần con (Composite Components)
  const isComposite = Boolean(source.isComposite);
  const components = isComposite && Array.isArray(source.components)
    ? source.components.map((c: any) => {
        const compProcesses = Array.isArray(c.processes) ? c.processes : [];
        const compMaterials = Array.isArray(c.materials) ? c.materials : [];

        const cProcIds = compProcesses.length > 0
          ? compProcesses.map((p: any) => p.processId || p.process?.id).filter(Boolean)
          : Array.isArray(c.processIds)
          ? c.processIds
          : [];

        const cProcConfigs = compProcesses.length > 0
          ? compProcesses.map((p: any) => ({
              processId: p.processId || p.process?.id,
              setupTimeMinutes: p.setupTimeMinutes ?? null,
              speedPerHour: p.speedPerHour ?? null,
              runWastePercent: p.runWastePercent != null ? Number(p.runWastePercent) : null,
              setupWasteQty: p.setupWasteQty != null ? Number(p.setupWasteQty) : null,
              isOutsourced: Boolean(p.isOutsourced),
              unitCost: p.unitCost != null ? Number(p.unitCost) : null,
              batchCost: p.batchCost != null ? Number(p.batchCost) : null,
            })).filter((x: any) => Boolean(x.processId))
          : Array.isArray(c.processConfigs)
          ? c.processConfigs
          : [];

        const cMatIds = compMaterials.length > 0
          ? compMaterials.map((m: any) => m.materialId || m.material?.id).filter(Boolean)
          : c.materialId
          ? [c.materialId]
          : Array.isArray(c.materialIds)
          ? c.materialIds
          : [];

        const cProdMaterials = compMaterials.length > 0
          ? compMaterials.map((m: any) => ({
              materialId: m.materialId || m.material?.id,
              standardQuantity: m.standardQuantity != null ? Number(m.standardQuantity) : 0,
              note: m.note || '',
            }))
          : Array.isArray(c.productMaterials)
          ? c.productMaterials
          : [];

        return {
          componentName: c.componentName || '',
          quantity: c.quantity != null ? Number(c.quantity) : 1,
          unit: c.unit || '',
          length: c.length != null ? Number(c.length) : undefined,
          width: c.width != null ? Number(c.width) : undefined,
          height: c.height != null ? Number(c.height) : undefined,
          flatLength: c.flatLength != null ? Number(c.flatLength) : undefined,
          flatWidth: c.flatWidth != null ? Number(c.flatWidth) : undefined,
          printFormat: c.printFormat || '',
          cutFormat: c.cutFormat || '',
          materialId: c.materialId || '',
          materialIds: cMatIds,
          productMaterials: cProdMaterials,
          processIds: cProcIds,
          processConfigs: cProcConfigs,
          process: c.process || '',
          specs: typeof c.specs === 'object' ? JSON.stringify(c.specs) : (c.specs || ''),
        };
      })
    : [];

  // 8. Kế thừa thông số bổ trợ & Specs
  const specs = typeof source.specs === 'object'
    ? JSON.stringify(source.specs)
    : (source.specs || '');

  return {
    productCode,
    productName,
    customerId,
    customerName,
    customer,
    typeId,
    productType,
    unit: source.unit || 'cái',
    packingSpec: source.packingSpec || '',
    pcsPerBox:
      source.pcsPerBox != null && Number(source.pcsPerBox) > 0
        ? Math.round(Number(source.pcsPerBox))
        : null,
    price: source.price != null ? Number(source.price) : 0,
    costPrice: source.costPrice != null ? Number(source.costPrice) : 0,
    length,
    width,
    height,
    flatLength,
    flatWidth,
    printFormat,
    cutFormat,
    outs,
    materialIds,
    productMaterials,
    processIds,
    processConfigs,
    isComposite,
    components,
    specs,
    process: source.process || '',
    tags: source.tags || '',
    image: source.image || '',
    active: true,
  };
}
